// Email service for compose and send functionality
import { API_CONFIG, EMAIL_API_ENDPOINTS, ML_API_ENDPOINTS } from '../helpers/constants';
import type { BusinessRulesValidationResult } from './businessRulesService';

const EMAIL_API_BASE_URL = API_CONFIG.EMAIL_API_BASE_URL;

export interface ComposeEmailRequest {
  from_email: string;
  to_emails: string[];
  cc_emails: string[];
  bcc_emails: string[];
  subject: string;
  content: string;
  content_type?: string; // Add content type to specify HTML
  gmail_message_id?: string;
  gmail_thread_id?: string;
  attachment_ids?: string[]; // Attachment IDs from upload
  delete_message_id?: string; // Message ID from draft response to delete
  /** True if user edited the generated draft before sending; false if sent as-is. Default false. */
  is_draft_edited?: boolean;
}

export interface ComposeEmailResponse {
  success: boolean;
  message: string;
  data?: any;
}

/** Response from POST /email/emailIds/search for To/Cc/Bcc suggestions */
export interface SearchEmailIdsResponse {
  emails: string[];
}

export const searchEmailIds = async (
  channelId: string,
  search: string,
  authToken: string
): Promise<SearchEmailIdsResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.EMAIL_IDS_SEARCH}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ channel_id: channelId, search }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let errorMessage = `Email IDs search failed: ${response.status}`;
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return { emails: Array.isArray(data?.emails) ? data.emails : [] };
  } catch (error) {
    console.error('Email IDs search error:', error);
    throw error;
  }
};

export const sendComposeEmail = async (
  data: ComposeEmailRequest,
  authToken: string
): Promise<ComposeEmailResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.COMPOSE_SEND}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Email send failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Email send error:', error);
    throw error;
  }
};

// Attachment upload interfaces and service
export interface UploadAttachmentsRequest {
  files: File[];
  draft_email_id?: string;
  thread_id?: string;
  channel_id?:string;
}

export interface AttachmentUploadItem {
  attachment_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface UploadAttachmentsResponse {
  success: boolean;
  attachments: AttachmentUploadItem[];
  // Legacy support
  attachment_id?: string;
  attachment_ids?: string[];
}

export const uploadAttachments = async (
  data: UploadAttachmentsRequest,
  authToken: string
): Promise<UploadAttachmentsResponse> => {
  try {
    const formData = new FormData();
    
    // Append files - API expects 'files' field
    data.files.forEach((file) => {
      formData.append('files', file);
    });
    
    if (data.draft_email_id) {
      formData.append('draft_email_id', data.draft_email_id);
    }
    
    if (data.thread_id) {
      formData.append('thread_id', data.thread_id);
    }

    if (data.channel_id) {
      formData.append('channel_id', data.channel_id);
    }

    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.UPLOAD_ATTACHMENTS}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Attachment upload failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    // Parse response according to API documentation format
    const responseData = await response.json();
    
    // Handle the documented API response format: { success: true, attachments: [...] }
    if (responseData.success && Array.isArray(responseData.attachments)) {
      const attachmentIds = responseData.attachments.map((item: AttachmentUploadItem) => item.attachment_id);
      return {
        success: true,
        attachments: responseData.attachments,
        attachment_id: attachmentIds.length > 0 ? attachmentIds[0] : undefined,
        attachment_ids: attachmentIds,
      };
    }
    
    // Legacy support for other response formats
    let attachmentIds: string[] = [];
    
    if (Array.isArray(responseData)) {
      attachmentIds = responseData;
    } else if (typeof responseData === 'string') {
      attachmentIds = responseData.split(',').map(id => id.trim()).filter(id => id);
    } else if (responseData.attachment_id) {
      attachmentIds = [responseData.attachment_id];
    } else if (responseData.attachment_ids) {
      attachmentIds = Array.isArray(responseData.attachment_ids) 
        ? responseData.attachment_ids 
        : [responseData.attachment_ids];
    } else if (responseData.attachments && Array.isArray(responseData.attachments)) {
      // Handle case where attachments array exists but not in documented format
      attachmentIds = responseData.attachments.map((item: any) => 
        item.attachment_id || item.id || item
      ).filter(Boolean);
    }

    return {
      success: responseData.success !== undefined ? responseData.success : true,
      attachments: responseData.attachments || [],
      attachment_id: attachmentIds.length > 0 ? attachmentIds[0] : undefined,
      attachment_ids: attachmentIds.length > 0 ? attachmentIds : [],
    };
  } catch (error) {
    console.error('Attachment upload error:', error);
    throw error;
  }
};

// Get attachment details interfaces and service
export interface AttachmentDetails {
  id?: string;
  attachment_id?: string;
  filename?: string;
  file_name?: string;
  original_filename?: string;
  file_size?: number;
  file_type?: string;
  content_type?: string;
  mime_type?: string;
  presigned_url?: string;
  /** Direct file URL when storage is local (used when VITE_DOWNLOAD_FILE is true) */
  file_url?: string;
  metadata?: {
    presigned_url?: string;
    presigned_url_expires_in?: number;
    original_filename?: string;
    inline?: boolean;
    [key: string]: any;
  };
  created_at?: string;
  updated_at?: string;
}

export interface GetAttachmentDetailsResponse {
  success: boolean;
  message?: string;
  data: AttachmentDetails;
}

export const getAttachmentDetails = async (
  attachmentId: string,
  authToken: string,
  includePresignedUrl: boolean = false
): Promise<GetAttachmentDetailsResponse> => {
  try {
    const response = await fetch(
      `${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_ATTACHMENT_DETAILS(attachmentId, includePresignedUrl)}`,
      {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to get attachment details: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    const responseData = await response.json();
    
    // Handle different response formats
    // The API returns: { success: true, data: { attachment: {...}, presigned_url: "..." } }
    // OR: { success: true, attachment: {...}, presigned_url: "..." }
    let attachmentData: AttachmentDetails;
    if (typeof responseData === 'string') {
      // If response is just a string (ID), create minimal object
      attachmentData = { id: responseData, attachment_id: responseData };
    } else if (responseData.data?.attachment) {
      // API returns: { success: true, data: { attachment: {...}, presigned_url/file_url } }
      // Preserve both presigned_url and file_url (file_url used when VITE_DISABLE_SUBSCRIPTION is true)
      attachmentData = {
        ...responseData.data.attachment,
        presigned_url: responseData.data.attachment.presigned_url || responseData.data.presigned_url,
        file_url: responseData.data.attachment.file_url || responseData.data.file_url,
      };
    } else if (responseData.data) {
      // Response has nested data property (attachment object directly in data)
      attachmentData = responseData.data;
    } else if (responseData.attachment) {
      // API returns: { success: true, attachment: {...}, presigned_url/file_url } (file_url when VITE_DOWNLOAD_FILE)
      attachmentData = {
        ...responseData.attachment,
        presigned_url: responseData.presigned_url || responseData.attachment.presigned_url,
        file_url: responseData.attachment.file_url || responseData.file_url,
      };
    } else {
      // Response is the attachment object directly
      attachmentData = responseData;
    }

    return {
      success: true,
      message: 'Attachment details fetched successfully',
      data: attachmentData,
    };
  } catch (error) {
    console.error('Get attachment details error:', error);
    throw error;
  }
};

// Draft attachment management interfaces and service
export interface AddAttachmentsToDraftRequest {
  attachment_ids: string[];
  thread_id?: string | null;
}

export interface RemoveAttachmentsFromDraftRequest {
  attachment_ids: string[];
}

export interface DraftAttachmentResponse {
  success: boolean;
  message: string;
  data?: any;
}

export interface HideDraftResponse {
  success: boolean;
  message: string;
  draft_id: string;
}

export const hideDraft = async (
  draftId: string,
  authToken: string
): Promise<HideDraftResponse> => {
  try {
    const response = await fetch(
      `${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.HIDE_DRAFT(draftId)}`,
      {
        method: 'PATCH',
        headers: {
          accept: 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      let errorMessage = `Failed to hide draft: ${response.status}`;

      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }

      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Hide draft error:', error);
    throw error;
  }
};

export const addAttachmentsToDraft = async (
  draftId: string,
  data: AddAttachmentsToDraftRequest,
  authToken: string
): Promise<DraftAttachmentResponse> => {
  try {
    const formData = new URLSearchParams();
    
    // API expects application/x-www-form-urlencoded
    // Try different formats - may need to adjust based on actual API behavior
    if (Array.isArray(data.attachment_ids)) {
      // Append each attachment_id separately or as JSON string
      // Based on API docs, it expects array<string>, so we'll try JSON stringified
      formData.append('attachment_ids', JSON.stringify(data.attachment_ids));
    }
    
    if (data.thread_id) {
      formData.append('thread_id', data.thread_id);
    }

    const response = await fetch(
      `${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.ADD_ATTACHMENTS_TO_DRAFT(draftId)}`,
      {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to add attachments to draft: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    const responseData = await response.json().catch(() => ({}));
    return {
      success: true,
      message: 'Attachments added to draft successfully',
      data: responseData,
    };
  } catch (error) {
    console.error('Add attachments to draft error:', error);
    throw error;
  }
};

export const removeAttachmentsFromDraft = async (
  draftId: string,
  data: RemoveAttachmentsFromDraftRequest,
  authToken: string
): Promise<DraftAttachmentResponse> => {
  try {
    const formData = new URLSearchParams();
    
    // API expects application/x-www-form-urlencoded
    if (Array.isArray(data.attachment_ids)) {
      formData.append('attachment_ids', JSON.stringify(data.attachment_ids));
    }

    const response = await fetch(
      `${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.REMOVE_ATTACHMENTS_FROM_DRAFT(draftId)}`,
      {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to remove attachments from draft: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    const responseData = await response.json().catch(() => ({}));
    return {
      success: true,
      message: 'Attachments removed from draft successfully',
      data: responseData,
    };
  } catch (error) {
    console.error('Remove attachments from draft error:', error);
    throw error;
  }
};

// Signature and salutation interfaces and service
export interface CreateSignatureSalutationRequest {
  channel_id: string;
  name: string;
  signature: string;
  salutation: string;
  is_default: boolean;
}

export interface CreateSignatureSalutationResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const createSignatureSalutation = async (
  data: CreateSignatureSalutationRequest,
  authToken: string
): Promise<CreateSignatureSalutationResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.CREATE_SIGNATURE_SALUTATION}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Signature salutation creation failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    // Handle 201 Created status - return success response
    if (response.status === 201) {
      const responseData = await response.json().catch(() => ({}));
      return {
        success: true,
        message: 'Signature and salutation created successfully',
        data: responseData
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Signature salutation creation error:', error);
    throw error;
  }
};

// Get signature salutations interfaces and service
export interface SignatureSalutationItem {
  id: string;
  channel_id: string;
  user_id: string;
  name: string;
  signature: string;
  salutation: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

export interface GetSignatureSalutationsAPIResponse {
  items: SignatureSalutationItem[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface GetSignatureSalutationsResponse {
  success: boolean;
  message: string;
  data: GetSignatureSalutationsAPIResponse;
}

export const getSignatureSalutations = async (
  channelId: string | null = null,
  page: number = 1,
  size: number = 10,
  authToken: string
): Promise<GetSignatureSalutationsResponse> => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (channelId) {
      params.append('channel_id', channelId);
    }

    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_SIGNATURE_SALUTATIONS}?${params}`, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Failed to fetch signature salutations: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    const responseData: GetSignatureSalutationsAPIResponse = await response.json();
    return {
      success: true,
      message: 'Signature salutations fetched successfully',
      data: responseData
    };
  } catch (error) {
    console.error('Get signature salutations error:', error);
    throw error;
  }
};

// Update signature and salutation interfaces and service
export interface UpdateSignatureSalutationRequest {
  channel_id: string;
  name: string;
  signature: string;
  salutation: string;
  is_default: boolean;
}

export interface UpdateSignatureSalutationResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const updateSignatureSalutation = async (
  signatureSalutationId: string,
  data: UpdateSignatureSalutationRequest,
  authToken: string
): Promise<UpdateSignatureSalutationResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.UPDATE_SIGNATURE_SALUTATION(signatureSalutationId)}`, {
      method: 'PUT',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Signature salutation update failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    const responseData = await response.json().catch(() => ({}));
    return {
      success: true,
      message: 'Signature and salutation updated successfully',
      data: responseData
    };
  } catch (error) {
    console.error('Signature salutation update error:', error);
    throw error;
  }
};

// Delete signature and salutation interfaces and service
export interface DeleteSignatureSalutationResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const deleteSignatureSalutation = async (
  signatureSalutationId: string,
  authToken: string
): Promise<DeleteSignatureSalutationResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.DELETE_SIGNATURE_SALUTATION(signatureSalutationId)}`, {
      method: 'DELETE',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Signature salutation deletion failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    // Handle successful deletion - return success response
    const responseData = await response.json().catch(() => ({}));
    return {
      success: true,
      message: 'Signature and salutation deleted successfully',
      data: responseData
    };
  } catch (error) {
    console.error('Signature salutation deletion error:', error);
    throw error;
  }
};

// Sub-label under a classification label in label_hierarchy (has own count/unread)
export interface LabelHierarchySubLabel {
  key: string;
  name: string;
  count: number;
  unread_count?: number;
  percentage: number;
}

// Label hierarchy item (classification label with sub_labels)
export interface LabelHierarchyItem {
  key: string;
  name: string;
  count: number;
  unread_count?: number;
  percentage: number;
  sub_labels: LabelHierarchySubLabel[];
}

// Email metrics interfaces and service
export interface EmailMetric {
  key: string;
  name: string;
  count: number;
  /** Unread count for this category/label; when present, UI shows this instead of count in badges */
  unread_count?: number;
  percentage: number;
}

export interface EmailMetricsData {
  channel_id: string;
  metrics: EmailMetric[];
  training_in_progress?: boolean;
  minimum_emails_to_train?: number;
  /** Classification labels with sub_labels (new API format) */
  label_hierarchy?: LabelHierarchyItem[];
}

export interface EmailMetricsResponse {
  success: boolean;
  data: EmailMetricsData;
}

export const getEmailMetrics = async (
  channelId: string,
  authToken: string
): Promise<EmailMetricsResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_EMAIL_METRICS(channelId)}`, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Failed to fetch email metrics: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Email metrics fetch error:', error);
    throw error;
  }
};

// Email sync status interfaces and service
export interface EmailSyncStatusData {
  email_address: string;
  workspace_id: string;
  channel_id: string;
  app_account: {
    exists: boolean;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  email_sync: {
    exists: boolean;
    is_active: boolean;
    last_sync: string | null;
    /** Next scheduled sync time (ISO string) */
    next_sync?: string | null;
    /** Next scheduled classification/model training time (ISO string) */
    next_classification_training?: string | null;
    sync_status: string;
    progress_percentage: number;
  };
  /** Topic/classification training last and next datetime (ISO strings) - at data root, not under email_sync */
  topic_training?: {
    last_topic_training?: string | null;
    next_topic_training?: string | null;
    /** When true, training is in progress; hide next time and retrain button until API returns false */
    training_inprogress?: boolean;
  };
  channel: {
    exists: boolean;
    channel_id: string;
    workspace_id: string;
    is_active: boolean;
    is_archived: boolean;
    created_at: string;
  };
  data_counts: {
    emails: number;
    threads: number;
    messages: number;
    vault_records: number;
    app_accounts: number;
    email_sync: number;
    email_hash: number;
    email_settings: number;
    signature_salutation: number;
    channel_members: number;
    datasources: number;
    channels: number;
    root_emails: number;
  };
}

export interface EmailSyncStatusResponse {
  success: boolean;
  data: EmailSyncStatusData;
}

export const getEmailSyncStatus = async (
  emailAddress: string,
  authToken: string,
  channelType?: 'email' | 'message' | 'voice'
): Promise<EmailSyncStatusResponse> => {
  // Only allow email sync status API calls for email channels
  if (channelType && channelType !== 'email') {
    throw new Error('Email sync status API can only be called for email channels');
  }
  
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.GET_EMAIL_SYNC_STATUS(emailAddress)}`, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle different error detail formats
      let errorMessage = `Failed to fetch email sync status: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          // Extract the first error message from the array
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Email sync status fetch error:', error);
    throw error;
  }
};

// --- Centralized Email Sync Status Polling Service ---

type SyncStatusSubscriber = (status: EmailSyncStatusResponse | null, error: Error | null) => void;

const syncStatusCache: { [email: string]: EmailSyncStatusResponse } = {};
const subscribers: { [email: string]: SyncStatusSubscriber[] } = {};
const pollingIntervals: { [email: string]: NodeJS.Timeout | null } = {};
const activeFetches: { [email: string]: boolean } = {};

const POLLING_INTERVAL = 60000; // 1 min

const notifySubscribers = (emailAddress: string, status: EmailSyncStatusResponse | null, error: Error | null) => {
  if (subscribers[emailAddress]) {
    subscribers[emailAddress].forEach(callback => callback(status, error));
  }
};

const stopPolling = (emailAddress: string) => {
  if (pollingIntervals[emailAddress]) {
    clearInterval(pollingIntervals[emailAddress]!);
    pollingIntervals[emailAddress] = null;
    console.log(`Polling stopped for ${emailAddress}`);
  }
};

const fetchAndNotify = async (emailAddress: string, authToken: string, channelType?: 'email' | 'message' | 'voice') => {
  if (activeFetches[emailAddress]) return;

  activeFetches[emailAddress] = true;

  try {
    const response = await getEmailSyncStatus(emailAddress, authToken, channelType);
    syncStatusCache[emailAddress] = response;
    notifySubscribers(emailAddress, response, null);

    const syncStatus = response.data.email_sync.sync_status;
    const isActive = response.data.email_sync.is_active;

    // Stop polling only when sync failed or connection is inactive (keep polling when success
    // so Channel Config and threads header get fresh last_sync / next_sync)
    if (syncStatus === 'failed' || !isActive) {
      stopPolling(emailAddress);
    }
  } catch (error) {
    notifySubscribers(emailAddress, null, error as Error);
    // Stop polling on error to prevent repeated failures for a bad request
    stopPolling(emailAddress);
  } finally {
    activeFetches[emailAddress] = false;
  }
};

export const subscribeToEmailSyncStatus = (
  emailAddress: string,
  authToken: string,
  callback: SyncStatusSubscriber,
  channelType?: 'email' | 'message' | 'voice'
) => {
  if (!emailAddress || channelType !== 'email') {
    return () => {}; // Return an empty unsubscribe function for non-email channels
  }

  if (!subscribers[emailAddress]) {
    subscribers[emailAddress] = [];
  }
  subscribers[emailAddress].push(callback);

  // If cached data exists, notify the new subscriber immediately
  if (syncStatusCache[emailAddress]) {
    callback(syncStatusCache[emailAddress], null);
  }

  // Start polling if not already started for this email address
  if (!pollingIntervals[emailAddress]) {
    console.log(`Starting polling for ${emailAddress}`);
    fetchAndNotify(emailAddress, authToken, channelType); // Initial fetch
    pollingIntervals[emailAddress] = setInterval(
      () => fetchAndNotify(emailAddress, authToken, channelType),
      POLLING_INTERVAL
    );
  }

  // Return an unsubscribe function
  return () => {
    const subs = subscribers[emailAddress];
    if (!subs) return;

    const index = subs.indexOf(callback);
    if (index > -1) {
      subs.splice(index, 1);
    }

    // If no more subscribers, clean up
    if (subs.length === 0) {
      stopPolling(emailAddress);
      delete subscribers[emailAddress];
      delete syncStatusCache[emailAddress];
      delete pollingIntervals[emailAddress];
    }
  };
};

/**
 * Force an immediate refetch of email sync status, update cache, and notify subscribers.
 * Use after reconnect/disconnect so the UI (Connection Status / Sync Status) updates right away.
 * Restarts polling if there are subscribers and it was stopped (e.g. after reconnect).
 */
export const refreshEmailSyncStatus = async (
  emailAddress: string,
  authToken: string,
  channelType?: 'email' | 'message' | 'voice'
): Promise<void> => {
  if (!emailAddress || channelType !== 'email') return;

  try {
    const response = await getEmailSyncStatus(emailAddress, authToken, channelType);
    syncStatusCache[emailAddress] = response;
    notifySubscribers(emailAddress, response, null);

    // Restart polling if we have subscribers but polling was stopped (e.g. after reconnect)
    if (subscribers[emailAddress]?.length && !pollingIntervals[emailAddress]) {
      pollingIntervals[emailAddress] = setInterval(
        () => fetchAndNotify(emailAddress, authToken, channelType),
        POLLING_INTERVAL
      );
    }
  } catch (error) {
    notifySubscribers(emailAddress, null, error as Error);
  }
};

export interface DisconnectEmailResponse {
  success: boolean;
  message: string;
}

export const disconnectEmailAccount = async (
  emailAddress: string,
  authToken: string,
  disconnectOption: string = 'stop_sync'
): Promise<DisconnectEmailResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.DISCONNECT_EMAIL(emailAddress, disconnectOption)}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to disconnect email account: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Disconnect email account error:', error);
    throw error;
  }
};

export interface ReconnectEmailResponse {
  success: boolean;
  message: string;
}

// Thrown by reconnectEmailAccount when the backend's 401 detail indicates the stored
// refresh token was revoked and a fresh OAuth consent is required (see
// docs/EmailReauth_401_Recovery_FE_Implementation.md §2.1 / §5.1).
export interface ReauthRequiredError extends Error {
  requiresReauth: true;
  reauthUrl: string;
  provider: string;
}

function isReauthDetail(detail: unknown): detail is { message: string; requires_reauth: true; reauth_url: string; provider: string } {
  return !!detail && typeof detail === 'object' && !Array.isArray(detail) && (detail as any).requires_reauth === true;
}

export const reconnectEmailAccount = async (
  emailAddress: string,
  authToken: string
): Promise<ReconnectEmailResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.RECONNECT_EMAIL(emailAddress)}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      // shay-email-backend's global HTTPException handler (main.py) rewraps every
      // HTTPException's `detail` into a top-level `message` field — `{error, message, path}`,
      // not FastAPI's default `{detail}` — so exc.detail (the reauth object this route raises)
      // arrives as errorData.message on the real deployed backend. Check both: `message` first
      // (matches actual runtime behavior), `detail` as a fallback (matches the documented
      // contract / a raw FastAPI response, in case that global wrapper is ever bypassed).
      const rawDetail = errorData.message ?? errorData.detail;

      if (isReauthDetail(rawDetail)) {
        const err = new Error(rawDetail.message) as ReauthRequiredError;
        err.requiresReauth = true;
        err.reauthUrl = rawDetail.reauth_url;
        err.provider = rawDetail.provider;
        throw err;
      }

      let errorMessage = `Failed to reconnect email account: ${response.status}`;

      if (rawDetail) {
        if (typeof rawDetail === 'string') {
          errorMessage = rawDetail;
        } else if (Array.isArray(rawDetail) && rawDetail.length > 0) {
          const firstError = rawDetail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }

      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Reconnect email account error:', error);
    throw error;
  }
};

export interface ReauthorizeEmailRequest {
  oauth_code?: string;
  redirect_uri?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  provider?: string;
}

export interface ReauthorizeEmailResponse {
  success: boolean;
  message: string;
  data: {
    email_address: string;
    app_account_id: string;
    channel_id: string;
    workspace_id: string;
    connection_status: string;
    sync_status: string;
    next_sync: string | null;
    requires_reauth: false;
  };
}

export const reauthorizeEmailAccount = async (
  emailAddress: string,
  payload: ReauthorizeEmailRequest,
  authToken: string
): Promise<ReauthorizeEmailResponse> => {
  const response = await fetch(
    `${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.REAUTHORIZE_EMAIL(emailAddress)}`,
    {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Same global-handler rewrap as reconnectEmailAccount above: the plain-string detail §2.3
    // describes arrives as errorData.message on the real backend, not errorData.detail.
    const rawDetail = errorData.message ?? errorData.detail;
    throw new Error(typeof rawDetail === 'string' ? rawDetail : `Failed to re-authorize: ${response.status}`);
  }

  return await response.json();
};

export interface DeleteEmailDataResponse {
  success: boolean;
  message: string;
}

// Move to inbox interfaces and service
export interface MoveToInboxRequest {
  external_app_id: string;
}

export interface MoveToInboxResponse {
  success: boolean;
  message: string;
  external_app_id: string;
  provider_thread_id: string;
  thread_id: string;
  emails_moved: number;
  previous_classification: string;
  new_classification: string;
}

/** Request body for move-to-classification API (moves whole thread) */
export interface MoveToClassificationRequest {
  external_app_id: string;
  channel_id: string;
}

/** Response from move-to-classification API */
export interface MoveToClassificationResponse {
  success: boolean;
  message: string;
  external_app_id?: string;
  provider_thread_id?: string;
  thread_id?: string;
  emails_moved?: number;
  previous_classification?: string;
  new_classification?: string;
}

/** Target classification for move: inbox, spam, no_reply */
export type EmailClassification = 'inbox' | 'spam' | 'no_reply';

/**
 * Move whole email thread to a target classification (inbox, spam, no_reply).
 * Uses POST /email/email/move-to/{classification} with external_app_id and channel_id.
 */
export const moveToClassification = async (
  classification: EmailClassification,
  data: MoveToClassificationRequest,
  authToken: string
): Promise<MoveToClassificationResponse> => {
  try {
    const response = await fetch(
      `${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.MOVE_TO_CLASSIFICATION(classification)}`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      let errorMessage = `Failed to move email to ${classification}: ${response.status}`;
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Move to classification error:', error);
    throw error;
  }
};

export const moveToInbox = async (
  data: MoveToInboxRequest,
  authToken: string
): Promise<MoveToInboxResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.MOVE_TO_INBOX}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to move email to inbox: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Move to inbox error:', error);
    throw error;
  }
};

export const deleteEmailData = async (
  emailAddress: string,
  authToken: string,
  archiveOnly: boolean = false
): Promise<DeleteEmailDataResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.DELETE_EMAIL_DATA(emailAddress, archiveOnly)}`, {
      method: 'DELETE',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to delete email data: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Delete email data error:', error);
    throw error;
  }
};

// IMAP/SMTP Email Connection interfaces and service
export interface ConnectEmailImapRequest {
  email: string;
  connection_method: string;
  app_password: string;
  imap_host: string;
  imap_port: number;
  historical_sync_days?: number;
  archive_to_s3?: boolean;
  archive_format?: string;
  workspace_id?: string;
  workspace_tag?: string;
  description?: string;
  /** List of category values (tags) for the channel */
  channel_tags?: string[];
  /** Per-tag subtags dictionary: { "Customer Support": [...], "HR": [...] } */
  channel_sub_tags?: Record<string, string[]>;
  /** Required by the backend when creating a new channel (max_length=4000 server-side) —
   * omitted (not sent as empty string) when reconnecting to an existing channel. */
  channel_business_rules?: string;
  validation_result?: BusinessRulesValidationResult;
  force_save?: boolean;
  email_config: {
    imap_settings: {
      server: string;
      port: number;
      ssl: boolean;
      security: string;
    };
    smtp_settings: {
      server: string;
      port: number;
      auth: boolean;
      ssl: boolean;
      security: string;
    };
  };
}

export interface ConnectEmailImapResponse {
  success: boolean;
  message: string;
  connection_method: string;
  app_account_id: string;
  vault_id: string;
  sync_status: string;
  oauth_provider: string;
  user_info: any;
  database_emails: number;
  s3_archive_url: string;
  channel_id: string;
  workspace_id: string;
  source_email_id: string;
  email_settings: any;
}

export const connectEmailImap = async (
  data: ConnectEmailImapRequest,
  authToken: string
): Promise<ConnectEmailImapResponse> => {
  try {
    const response = await fetch(`${EMAIL_API_BASE_URL}${EMAIL_API_ENDPOINTS.CONNECT}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Email connection failed: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Email IMAP connection error:', error);
    throw error;
  }
};

// Generate Description interfaces and service
export interface GenerateDescriptionRequest {
  email_id: string;
  workspace_name: string;
  categories: string[]; // Categories field (from tag dropdown) - required
  tags: string[]; // Tags field (from keywords)
}

export interface GenerateDescriptionResponse {
  status: string;
  message: string;
  descriptions: string[];
}

export const generateDescription = async (
  data: GenerateDescriptionRequest,
  authToken: string,
  signal?: AbortSignal
): Promise<GenerateDescriptionResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.GENERATE_DESCRIPTION}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      signal, // Add abort signal support
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to generate descriptions: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error: any) {
    // Don't log AbortError as it's expected when requests are cancelled
    if (error.name !== 'AbortError' && !error.message?.includes('aborted')) {
      console.error('Generate description error:', error);
    }
    throw error;
  }
};

// Generate Keywords interfaces and service
export interface GenerateKeywordsRequest {
  email_id: string;
  workspace_name: string;
  categories: string[];
}

export interface GenerateKeywordsResponse {
  status: string;
  message: string;
  keywords: string[];
  keywords_map?: Record<string, string[]>;
}

export const generateKeywords = async (
  data: GenerateKeywordsRequest,
  authToken: string
): Promise<GenerateKeywordsResponse> => {
  try {
    const response = await fetch(`${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.GENERATE_KEYWORDS}`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      let errorMessage = `Failed to generate keywords: ${response.status}`;
      
      if (errorData.detail) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
          const firstError = errorData.detail[0];
          if (firstError.msg) {
            errorMessage = firstError.msg;
          } else if (typeof firstError === 'string') {
            errorMessage = firstError;
          }
        }
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Generate keywords error:', error);
    throw error;
  }
};